import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MqttSuspense } from "@/components/generic/mqtt-suspense";
import { ActiveJobCard } from "@/components/milling-jobs/active-job-card";
import { ExecutedJobList } from "@/components/milling-jobs/executed-job-list";
import { QueuedJobList } from "@/components/milling-jobs/queued-job-list";
import { RealTimeDataWaitingAlert } from "@/components/status/real-time-data-waiting-alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type ApiError, isApiWaitingOrError } from "@/data-interfaces/generics";
import type {
	ExecutedMillingJob,
	QueuedMillingJob,
} from "@/data-interfaces/milling-jobs";
import type { RealTimeData } from "@/data-interfaces/real-time-data";
import {
	useMQTTAutoRefreshPublish,
	useMQTTPublish,
	useMQTTSubscribe,
} from "@/hooks/use-mqtt";
import { getAppWrappedPageUrl, MILLING_JOBS_PAGE_URL } from "@/lib/page-routes";
import {
	getRtdSubscriptionTopic,
	MJ_EXECUTED_JOBS_URL,
	MJ_QUEUED_JOBS_URL,
} from "@/lib/request-paths";
import { getMachineSerialNumber } from "@/lib/session-storage-keys";

export const Route = createFileRoute(
	getAppWrappedPageUrl(MILLING_JOBS_PAGE_URL),
)({
	component: MillingJobs,
	validateSearch: (search: Record<string, unknown>) => {
		return search.selectedTab ?? { selectedTab: "milling" };
	},
});

function MillingJobs() {
	const [t, _] = useTranslation();

	const { selectedTab } = Route.useSearch();

	const [realTimeData, setRealTimeData] = useState<
		RealTimeData | ApiError | null
	>(null);
	const [queuedJobs, setQueuedJobs] = useState<
		Array<QueuedMillingJob> | ApiError | null
	>(null);
	const [executedJobs, setExecutedJobs] = useState<
		Array<ExecutedMillingJob> | ApiError | null
	>(null);

	const [currentMillingJobId, setCurrentMillingJobId] = useState<number | null>(
		null,
	);

	const publish = useMQTTPublish();

	useMQTTSubscribe(
		getRtdSubscriptionTopic(getMachineSerialNumber()),
		setRealTimeData,
		true,
	);
	useMQTTSubscribe(MJ_QUEUED_JOBS_URL, setQueuedJobs);
	useMQTTSubscribe(MJ_EXECUTED_JOBS_URL, setExecutedJobs);

	useMQTTAutoRefreshPublish(20, MJ_QUEUED_JOBS_URL);
	useMQTTAutoRefreshPublish(0, MJ_EXECUTED_JOBS_URL);

	const refreshQueuedAndExecutedJobs = () => {
		publish(MJ_QUEUED_JOBS_URL);
		publish(MJ_EXECUTED_JOBS_URL);
	};

	useEffect(() => {
		if (!isApiWaitingOrError(realTimeData)) {
			if (
				realTimeData.milling_job &&
				realTimeData.milling_job.id !== currentMillingJobId
			) {
				setCurrentMillingJobId(realTimeData.milling_job.id);
				refreshQueuedAndExecutedJobs();
			} else if (
				realTimeData.milling_job === null &&
				currentMillingJobId !== null
			) {
				setCurrentMillingJobId(null);
				refreshQueuedAndExecutedJobs();
			}
		}
	}, [realTimeData]);

	return (
		<div className="text-white p-8 flex flex-col grow">
			<h1 className="text-3xl sm:text-4xl font-medium">{t("Jobs")}</h1>

			<div className="flex flew-row flex-1 justify-center min-[1970px]:hidden mt-4">
				<Tabs
					defaultValue={selectedTab}
					className="flex flex-col flex-1 gap-y-4 items-center"
				>
					<TabsList>
						<TabsTrigger value="milling" className="min-w-25">
							{t("Milling")}
						</TabsTrigger>
						<TabsTrigger value="queue" className="min-w-25">
							{t("Queue")}
						</TabsTrigger>
						<TabsTrigger value="history" className="min-w-25">
							{t("History")}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="milling">
						<MqttSuspense
							objects={realTimeData}
							genericError="Error retriving real-time data"
						>
							{(realTimeData) =>
								realTimeData.milling_job ? (
									<ActiveJobCard active_job={realTimeData.milling_job} />
								) : (
									<div className="text-xl sm:text-3xl">
										{t("No active job")}
									</div>
								)
							}
						</MqttSuspense>

						<RealTimeDataWaitingAlert dataToWait={realTimeData} />
					</TabsContent>

					<TabsContent value="queue" className="flex flex-col flex-1">
						<MqttSuspense
							objects={queuedJobs}
							requestUrl={MJ_QUEUED_JOBS_URL}
							genericError="Error retriving jobs"
						>
							{(queuedJobs) => <QueuedJobList queued_jobs={queuedJobs} />}
						</MqttSuspense>
					</TabsContent>

					<TabsContent value="history" className="flex flex-col flex-1">
						<MqttSuspense
							objects={executedJobs}
							requestUrl={MJ_EXECUTED_JOBS_URL}
							genericError="Error retriving jobs"
						>
							{(executedJobs) => (
								<ExecutedJobList executed_jobs={executedJobs} />
							)}
						</MqttSuspense>
					</TabsContent>
				</Tabs>
			</div>

			<div className="hidden flex-row flex-1 gap-x-8 pt-4 min-[1970px]:flex">
				<div className="flex flex-col flex-1 grow-2 gap-y-4">
					<div className="flex flex-col gap-y-4">
						<h2 className="text-3xl">{t("Milling")}</h2>

						<MqttSuspense
							objects={realTimeData}
							genericError="Error retriving real-time data"
						>
							{(realTimeData) =>
								realTimeData.milling_job ? (
									<ActiveJobCard active_job={realTimeData.milling_job} />
								) : (
									<div className="text-3xl">{t("No active job")}</div>
								)
							}
						</MqttSuspense>

						<RealTimeDataWaitingAlert dataToWait={realTimeData} />
					</div>

					<div className="flex flex-col grow gap-y-4">
						<h2 className="text-3xl">{t("History")}</h2>

						<MqttSuspense
							objects={executedJobs}
							requestUrl={MJ_EXECUTED_JOBS_URL}
							genericError="Error retriving jobs"
						>
							{(executedJobs) => (
								<ExecutedJobList executed_jobs={executedJobs} />
							)}
						</MqttSuspense>
					</div>
				</div>

				<div className="flex flex-col flex-1 gap-y-4 pl-4 border-l border-l-white">
					<h2 className="text-3xl">
						{t("Queue")}
						{" ("}
						{!isApiWaitingOrError(queuedJobs) ? queuedJobs.length : "-"}{" "}
						{t("jobs")}
						{")"}
					</h2>

					<MqttSuspense
						objects={queuedJobs}
						requestUrl={MJ_QUEUED_JOBS_URL}
						genericError="Error retriving jobs"
					>
						{(queuedJobs) => <QueuedJobList queued_jobs={queuedJobs} />}
					</MqttSuspense>
				</div>
			</div>
		</div>
	);
}
